// services/socket.ts
// Enhanced WebSocket service for the improved multi-agent system
// with support for progress updates, clarification questions, and structured research results

import { API_CONFIG } from '../config/api';

let socket: WebSocket | null = null;
let listeners: ((data: any) => void)[] = [];
let messageQueue: any[] = [];
let isConnecting = false;
let reconnectAttempts = 0;
let maxReconnectAttempts = 5;
let reconnectDelay = 1000; // Start with 1 second
let maxReconnectDelay = 30000; // Max 30 seconds
let reconnectTimer: NodeJS.Timeout | null = null;
let intentionalDisconnect = false;
let currentJwtToken: string | undefined = undefined; // Store JWT for reconnections

export function initTemplateSocket(jwtToken: string): void {
  // Store JWT token for reconnections
  currentJwtToken = jwtToken;
  
  // Use template execution endpoint from config
  const baseUrl = API_CONFIG.endpoints.websocket.template;
  const wsUrl = `${baseUrl}?token=${encodeURIComponent(jwtToken)}`;
  
  console.log("DEBUG: initTemplateSocket called");
  console.log("DEBUG: Template WebSocket URL:", wsUrl.substring(0, 100) + "...");
  
  return createWebSocketConnection(wsUrl);
}

function createWebSocketConnection(wsUrl: string) {
  console.log("DEBUG: Creating template WebSocket connection to:", wsUrl);
  
  // Close existing socket if any
  if (socket) {
    socket.close();
  }
  
  isConnecting = true;
  socket = new WebSocket(wsUrl);
  
  socket.onopen = () => {
    console.log("✅ Template WebSocket connected successfully");
    isConnecting = false;
    reconnectAttempts = 0;
    
    // Process queued messages
    while (messageQueue.length > 0) {
      const message = messageQueue.shift();
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(message));
      }
    }
  };
  
  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      console.log("📨 Template WebSocket received:", data);
      
      // Notify all listeners
      listeners.forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          console.error("Error in WebSocket listener:", error);
        }
      });
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  };
  
  socket.onclose = () => {
    console.log("🔌 Template WebSocket closed");
    isConnecting = false;
    socket = null;
  };
  
  socket.onerror = (error) => {
    console.error("❌ Template WebSocket error:", error);
    isConnecting = false;
  };
}

export function initSocket(jwtToken?: string, useTemplateEndpoint: boolean = false) {
  // Store JWT token for reconnections
  if (jwtToken) {
    currentJwtToken = jwtToken;
  }
  
  // Use stored token if not provided (for reconnections)
  const tokenToUse = jwtToken || currentJwtToken;
  
  // CRITICAL: Don't connect without a token
  if (!tokenToUse) {
    console.error("ERROR: Cannot initialize WebSocket without JWT token");
    // Notify listeners about authentication error
    const authError = {
      type: 'websocket_error',
      error: 'Authentication token missing',
      message: 'Please log out and log back in to continue'
    };
    listeners.forEach(listener => {
      try {
        listener(authError);
      } catch (error) {
        console.error("Error in WebSocket auth error listener:", error);
      }
    });
    return;
  }
  
  // Choose endpoint based on template execution flag
  const baseUrl = useTemplateEndpoint 
    ? API_CONFIG.endpoints.websocket.template 
    : API_CONFIG.endpoints.websocket.research;
  const wsUrl = `${baseUrl}?token=${encodeURIComponent(tokenToUse)}`;
  
  console.log("DEBUG: initSocket called");
  console.log("DEBUG: useTemplateEndpoint:", useTemplateEndpoint);
  console.log("DEBUG: Selected baseUrl:", baseUrl);
  console.log("DEBUG: WebSocket URL:", wsUrl.substring(0, 100) + "...");
  console.log("DEBUG: JWT Token provided:", !!tokenToUse);
  console.log("DEBUG: Current socket state:", socket ? socket.readyState : "null");
  console.log("DEBUG: isConnecting:", isConnecting);

  if (isConnecting) {
    console.log("DEBUG: Socket connection already in progress...");
    return;
  }

  if (!socket || socket.readyState === WebSocket.CLOSED) {
    // Close any existing connection before creating new one
    if (socket && socket.readyState !== WebSocket.CLOSED) {
      console.log("DEBUG: Closing existing WebSocket before creating new one");
      socket.close();
    }
    
    isConnecting = true;
    console.log("DEBUG: Connecting to enhanced multi-agent WebSocket...");
    console.log("DEBUG: Creating new WebSocket with URL:", wsUrl);
    try {
      socket = new WebSocket(wsUrl);
      console.log("DEBUG: WebSocket created successfully");
    } catch (error) {
      console.error("DEBUG: Failed to create WebSocket:", error);
      isConnecting = false;
      return;
    }

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("Enhanced WebSocket received:", data);
        
        // Notify all listeners with the received data
        listeners.forEach(listener => {
          try {
            listener(data);
          } catch (error) {
            console.error("Error in WebSocket listener:", error);
          }
        });
      } catch (error) {
        console.error("Error parsing enhanced WebSocket message:", error);
      }
    };

    socket.onopen = () => {
      console.log("DEBUG: Enhanced multi-agent WebSocket connected successfully!");
      console.log("DEBUG: WebSocket readyState:", socket?.readyState);
      console.log("DEBUG: WebSocket URL:", socket?.url);
      isConnecting = false;
      reconnectAttempts = 0; // Reset reconnection attempts on successful connection
      reconnectDelay = 1000; // Reset delay
      
      // Clear any pending reconnection timer
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      
      // Notify all listeners about successful connection
      const connectionEvent = {
        type: 'websocket_connected',
        message: 'Connection established successfully'
      };
      
      listeners.forEach(listener => {
        try {
          listener(connectionEvent);
        } catch (error) {
          console.error("Error in WebSocket connection listener:", error);
        }
      });
      
      // Process any queued messages with a small delay to ensure connection is fully ready
      if (messageQueue.length > 0) {
        console.log("DEBUG: Processing", messageQueue.length, "queued messages");
        setTimeout(() => {
          while (messageQueue.length > 0) {
            const queuedMessage = messageQueue.shift();
            if (socket && socket.readyState === WebSocket.OPEN) {
              console.log("DEBUG: Sending queued message:", queuedMessage);
              socket.send(queuedMessage);
            }
          }
        }, 100); // Small delay to ensure connection is fully established
      }
    };

    socket.onclose = (event) => {
      console.log("Enhanced WebSocket closed. Code:", event.code, "Reason:", event.reason);
      isConnecting = false;
      
      // Notify all listeners about the disconnection
      const disconnectionEvent = {
        type: 'websocket_disconnected',
        code: event.code,
        reason: event.reason,
        wasClean: event.code === 1000,
        reconnectAttempts
      };
      
      listeners.forEach(listener => {
        try {
          listener(disconnectionEvent);
        } catch (error) {
          console.error("Error in WebSocket disconnection listener:", error);
        }
      });
      
      // Attempt to reconnect with exponential backoff (unless intentionally disconnected or clean close)
      if (!intentionalDisconnect && event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++;
        const delay = Math.min(reconnectDelay * Math.pow(2, reconnectAttempts - 1), maxReconnectDelay);
        console.log(`Attempting to reconnect in ${delay}ms... (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
        
        reconnectTimer = setTimeout(() => {
          if (reconnectAttempts <= maxReconnectAttempts) {
            initSocket();
          }
        }, delay);
      } else if (reconnectAttempts >= maxReconnectAttempts) {
        console.error("Max reconnection attempts reached. Please refresh the page to reconnect.");
        // Notify listeners that reconnection failed
        const reconnectFailedEvent = {
          type: 'websocket_reconnect_failed',
          message: 'Max reconnection attempts reached',
          attempts: reconnectAttempts
        };
        listeners.forEach(listener => {
          try {
            listener(reconnectFailedEvent);
          } catch (error) {
            console.error("Error in WebSocket reconnect failed listener:", error);
          }
        });
      }
    };

    socket.onerror = (err) => {
      console.error("Enhanced WebSocket error:", err);
      isConnecting = false;
      
      // Notify all listeners about the error
      const errorEvent = {
        type: 'websocket_error',
        error: err,
        message: 'WebSocket connection error occurred'
      };
      
      listeners.forEach(listener => {
        try {
          listener(errorEvent);
        } catch (error) {
          console.error("Error in WebSocket error listener:", error);
        }
      });
    };
  }
}

export function sendMessageWS(messageData: string | object) {
  // Ensure message is properly stringified
  const stringMessage = typeof messageData === 'string' ? messageData : JSON.stringify(messageData);
  
  console.log("DEBUG: sendMessageWS called with:", messageData);
  console.log("DEBUG: Stringified message:", stringMessage);
  console.log("DEBUG: Socket exists:", !!socket);
  console.log("DEBUG: Socket readyState:", socket ? socket.readyState : "null");
  console.log("DEBUG: WebSocket.OPEN:", WebSocket.OPEN);
  
  if (socket && socket.readyState === WebSocket.OPEN) {
    console.log("DEBUG: Sending enhanced message:", stringMessage);
    try {
      socket.send(stringMessage);
      console.log("DEBUG: Message sent successfully");
    } catch (error) {
      console.error("DEBUG: Failed to send message:", error);
    }
  } else {
    console.warn("DEBUG: Enhanced WebSocket not connected. Queueing message:", stringMessage);
    console.warn("DEBUG: Socket state details:");
    console.warn("   - Socket exists:", !!socket);
    console.warn("   - Socket readyState:", socket ? socket.readyState : "null");
    console.warn("   - Socket url:", socket ? socket.url : "null");
    console.warn("   - isConnecting:", isConnecting);
    
    // Queue the message to be sent when connection is established
    messageQueue.push(stringMessage);
    
    // Try to connect if not already connecting
    if (!isConnecting) {
      console.log("DEBUG: Attempting to reconnect...");
      initSocket();
    } else {
      console.log("DEBUG: Connection already in progress, message queued");
    }
  }
}

export function subscribe(callback: (data: any) => void) {
  listeners.push(callback);
  console.log("Subscribed to enhanced WebSocket. Total listeners:", listeners.length);
}

export function unsubscribe(callback: (data: any) => void) {
  const initialLength = listeners.length;
  listeners = listeners.filter(fn => fn !== callback);
  console.log("Unsubscribed from enhanced WebSocket. Listeners:", initialLength, "->", listeners.length);
}

export function closeSocket() {
  intentionalDisconnect = true; // Mark as intentional to prevent reconnection
  
  // Clear any pending reconnection timer
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  
  if (socket) {
    socket.close(1000, "User initiated close");
    socket = null;
  }
  
  // Reset state
  listeners = [];
  messageQueue = [];
  isConnecting = false;
  reconnectAttempts = 0;
  reconnectDelay = 1000;
  intentionalDisconnect = false; // Reset for next connection
}

export function getSocketState() {
  return socket ? socket.readyState : WebSocket.CLOSED;
}

// Enhanced message sending functions for specific message types

export function sendQuery(message: string, apiKey?: string) {
  const messageData: any = {
    message,
    type: 'query'
  };
  
  // SECURITY: Only include API key if provided
  if (apiKey) {
    messageData.api_key = apiKey;
  }
  
  sendMessageWS(JSON.stringify(messageData));
}

export function sendClarificationResponse(clarificationAnswers: {[key: string]: string}) {
  const messageData = {
    message: "Continue with research",
    type: 'clarification_response',
    clarification_answers: clarificationAnswers
  };
  sendMessageWS(JSON.stringify(messageData));
}

export function sendFollowUpQuestion(message: string, context?: any) {
  const messageData = {
    message,
    type: 'followup',
    context
  };
  sendMessageWS(JSON.stringify(messageData));
}
